import {
  Banknote,
  FileCheck2,
  FileCog,
  BarChart3,
  BookOpenCheck,
  Building2,
  ClipboardList,
  FilePlus2,
  FileText,
  KanbanSquare,
  LayoutDashboard,
  Package,
  PhoneCall,
  ReceiptText,
  ScrollText,
  ShieldAlert,
  UserCog,
  UserPlus,
  Users,
  Wallet,
  Landmark,
  ListTree,
  MapPinned,
  type LucideIcon,
} from 'lucide-react'
import type { Permission } from '@tumin/domain'
import { grants, type PermissionRequirement } from '@/lib/permissions'
import { ROUTES, type AppRoute } from '@/lib/routes'

/** Grupos de la navegación, en el orden en que aparecen. */
export const NAV_GROUPS = [
  { id: 'main', label: 'Principal' },
  { id: 'operations', label: 'Operación' },
  { id: 'control', label: 'Control' },
  { id: 'admin', label: 'Administración' },
] as const
export type NavGroupId = (typeof NAV_GROUPS)[number]['id']

/** Entrada de la navegación principal. */
export interface NavItem {
  id: string
  label: string
  to: AppRoute
  icon: LucideIcon
  group: NavGroupId
  /** Permiso requerido para ver la entrada (autorización por permiso, nunca por rol). */
  permission: PermissionRequirement
  /** Segunda tecla del atajo `g + tecla` (p. ej. 'c' → G C). */
  shortcutKey?: string
  /**
   * Prioridad en la barra inferior del teléfono (menor = primero). Sin valor
   * la entrada solo aparece en "Más".
   */
  mobilePriority?: number
  /** Marca activa solo con coincidencia exacta de ruta. */
  exact?: boolean
  /** Palabras extra para la paleta de comandos. */
  keywords?: string
}

/**
 * Quien captura en campo sin señal: gestiones de cobro, clientes nuevos o
 * solicitudes. Da acceso a la pantalla "Campo".
 */
export const FIELD_WORK_PERMISSION: PermissionRequirement = { anyOf: ['collections:manage', 'clients:write', 'loans:originate'] }

/**
 * Configuración ÚNICA de la navegación: la barra lateral, la barra inferior
 * móvil, la paleta de comandos y los atajos `g + tecla` se construyen de aquí.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { id: 'dashboard', label: 'Panel', to: ROUTES.dashboard, icon: LayoutDashboard, group: 'main', permission: undefined, shortcutKey: 'd', mobilePriority: 1, keywords: 'inicio indicadores resumen' },
  { id: 'clients', label: 'Clientes', to: ROUTES.clients, icon: Users, group: 'main', permission: 'clients:read', shortcutKey: 'c', mobilePriority: 2, keywords: 'personas deudores asociados' },
  { id: 'pipeline', label: 'Solicitudes', to: ROUTES.pipeline, icon: KanbanSquare, group: 'main', permission: 'loans:read', shortcutKey: 's', mobilePriority: 3, exact: true, keywords: 'tablero originacion comite kanban' },
  { id: 'loans', label: 'Cartera', to: ROUTES.loans, icon: Landmark, group: 'main', permission: 'loans:read', shortcutKey: 'l', exact: true, keywords: 'prestamos creditos vigentes' },
  { id: 'payments', label: 'Pagos', to: ROUTES.payments, icon: ReceiptText, group: 'operations', permission: 'payments:read', shortcutKey: 'p', mobilePriority: 5, keywords: 'cobros cuotas recibos abonos' },
  { id: 'cash', label: 'Caja', to: ROUTES.cash, icon: Wallet, group: 'operations', permission: { anyOf: ['cash:operate', 'cash:supervise'] }, shortcutKey: 'j', mobilePriority: 5, keywords: 'arqueo cierre apertura efectivo' },
  { id: 'collections', label: 'Cobranza', to: ROUTES.collections, icon: PhoneCall, group: 'operations', permission: 'collections:read', shortcutKey: 'm', mobilePriority: 4, keywords: 'mora gestiones promesas visitas' },
  { id: 'field', label: 'Campo', to: ROUTES.field, icon: MapPinned, group: 'operations', permission: FIELD_WORK_PERMISSION, shortcutKey: 'v', mobilePriority: 4, keywords: 'sin conexion sin señal offline visitas ruta pendientes sincronizar' },
  { id: 'compliance', label: 'Cumplimiento', to: ROUTES.compliance, icon: ShieldAlert, group: 'control', permission: 'compliance:read', shortcutKey: 'k', keywords: 'ive lavado alertas pep' },
  { id: 'invoices', label: 'Facturas', to: ROUTES.invoices, icon: FileCheck2, group: 'control', permission: 'billing:read', shortcutKey: 'f', keywords: 'fel sat factura electronica dte iva nota de credito' },
  { id: 'reports', label: 'Reportes', to: ROUTES.reports, icon: BarChart3, group: 'control', permission: 'reports:read', shortcutKey: 'r', keywords: 'informes cartera exportar' },
  { id: 'accounting', label: 'Contabilidad', to: ROUTES.accounting, icon: BookOpenCheck, group: 'control', permission: 'accounting:read', shortcutKey: 'n', keywords: 'partidas diario balanza mayor cierre contable' },
  { id: 'institution', label: 'Institución', to: ROUTES.institution, icon: Building2, group: 'admin', permission: 'settings:manage', shortcutKey: 'i', keywords: 'configuracion marca permisos feriados' },
  { id: 'documents', label: 'Documentos', to: ROUTES.documents, icon: FileText, group: 'admin', permission: 'settings:manage', shortcutKey: 'e', keywords: 'pdf contrato recibo plantillas clausulas logo papel' },
  { id: 'fel-settings', label: 'Facturación', to: ROUTES.felSettings, icon: FileCog, group: 'admin', permission: 'settings:manage', shortcutKey: 'x', keywords: 'fel sat certificador infile nit iva frases establecimiento' },
  { id: 'branches', label: 'Agencias', to: ROUTES.branches, icon: Banknote, group: 'admin', permission: 'catalog:manage', shortcutKey: 'a', keywords: 'sucursales oficinas' },
  { id: 'products', label: 'Productos', to: ROUTES.products, icon: Package, group: 'admin', permission: 'catalog:manage', shortcutKey: 'o', keywords: 'productos de credito tasas plazos' },
  { id: 'users', label: 'Usuarios', to: ROUTES.users, icon: UserCog, group: 'admin', permission: 'employees:manage', shortcutKey: 'u', keywords: 'empleados roles personal' },
  { id: 'accounting-accounts', label: 'Catálogo contable', to: ROUTES.accountingAccounts, icon: ListTree, group: 'admin', permission: 'accounting:manage', shortcutKey: 't', keywords: 'cuentas contables nomenclatura codigos' },
  { id: 'audit', label: 'Bitácora', to: ROUTES.audit, icon: ScrollText, group: 'admin', permission: 'audit:read', shortcutKey: 'b', keywords: 'auditoria historial cambios' },
]

/** Acción rápida de creación (atajos `c + tecla` y botones del panel). */
export interface CreateAction {
  id: string
  label: string
  to: AppRoute
  icon: LucideIcon
  permission: PermissionRequirement
  shortcutKey: string
  keywords?: string
}

/** Acciones de creación disponibles desde cualquier pantalla. */
export const CREATE_ACTIONS: readonly CreateAction[] = [
  { id: 'new-loan', label: 'Nueva solicitud', to: ROUTES.newLoan, icon: FilePlus2, permission: 'loans:originate', shortcutKey: 's', keywords: 'credito prestamo solicitar' },
  { id: 'new-client', label: 'Nuevo cliente', to: ROUTES.newClient, icon: UserPlus, permission: 'clients:write', shortcutKey: 'c', keywords: 'registrar persona' },
  { id: 'new-payment', label: 'Registrar pago', to: ROUTES.newPayment, icon: ClipboardList, permission: 'payments:post', shortcutKey: 'p', keywords: 'cobro cuota recibo' },
]

/** Filtra entradas por los permisos del usuario. */
export function filterByPermission<T extends { permission: PermissionRequirement }>(
  items: readonly T[],
  permissions: readonly Permission[],
): T[] {
  return items.filter((item) => grants(permissions, item.permission))
}

/**
 * Máximo de destinos en la barra inferior del teléfono, además de "Más".
 * Cinco caben en un teléfono de 360 px y dejan a mano Campo para quien
 * trabaja en ruta, sin sacar Solicitudes ni Cobranza.
 */
export const MOBILE_NAV_SLOTS = 5

/**
 * Divide las entradas visibles entre la barra inferior (por prioridad) y el
 * menú "Más". A igual prioridad (Pagos/Caja) gana la primera en el orden de
 * configuración.
 */
export function splitMobileNavigation(items: readonly NavItem[]): { primary: NavItem[]; overflow: NavItem[] } {
  const ranked = items
    .filter((item) => item.mobilePriority !== undefined)
    .sort((a, b) => (a.mobilePriority ?? 0) - (b.mobilePriority ?? 0))
  const primary = ranked.slice(0, MOBILE_NAV_SLOTS)
  const ids = new Set(primary.map((item) => item.id))
  return { primary, overflow: items.filter((item) => !ids.has(item.id)) }
}

/** Agrupa las entradas según `NAV_GROUPS`, omitiendo grupos vacíos. */
export function groupNavigation(items: readonly NavItem[]): { id: NavGroupId; label: string; items: NavItem[] }[] {
  return NAV_GROUPS.map((group) => ({ ...group, items: items.filter((item) => item.group === group.id) })).filter(
    (group) => group.items.length > 0,
  )
}
